/**
 * コスト指標の cross-product contract (PRODUCT_DEV-724)。
 *
 * ============================================================================
 * **S3 に保存される形が、そのまま ops と admin の契約**である。
 * ============================================================================
 *
 * 生産者は ops の日次コスト通知 Lambda ただ 1 つ:
 *
 *   外部 API (AWS CE / GitHub billing / OpenAI) を取得
 *     → **この contract の型**を組み立てる
 *     → S3 (`umeee-cost-metrics-*`) へ保存
 *     → その型から Slack の文面を作って投稿        … ops (blocks.ts)
 *
 * 消費者は 2 つで、どちらも **同じ型を受け取る**:
 *   - ops   : contract → Slack blocks
 *   - admin : contract → 画面用に集約 (GET /api/cost-metrics)
 *
 * こうしてあるのは「Slack に出た数字」と「画面の数字」を**構造的に**一致させる
 * ため。片方が内部型から直接作られていると、内部型を変えたときにもう片方が
 * 静かにずれる (形は合っているのに中身が違う、という気づけない壊れ方をする)。
 *
 * ---- 変更するときの約束 ----
 * - **フィールド追加は自由。削除・改名は破壊的変更**。ops と admin は別々に
 *   デプロイされるので、「ops が新形式を書いて admin はまだ旧版」という瞬間が
 *   必ず存在する。
 * - 破壊的変更をするときは `COST_PAYLOAD_VERSION` を上げる。admin は自分が
 *   知らない version を「データなし」として扱い、誤った数字を出さない。
 */

import { z } from "zod";

// ---------------------------------------------------------------------------
// 保存先のキー (ops が書き、admin が読む。文字列を両側に散らさない)
// ---------------------------------------------------------------------------

export const COST_METRICS_PREFIX = "cost-metrics/";
export const COST_LATEST_KEY = `${COST_METRICS_PREFIX}latest.json`;

/**
 * payload のスキーマ版。**破壊的変更のときだけ上げる。**
 * admin は不一致を検出したら読まない (古い admin が新形式を誤読しないため)。
 */
export const COST_PAYLOAD_VERSION = 1;

// ---------------------------------------------------------------------------
// 日次ロールアップ (推移グラフの素)
// ---------------------------------------------------------------------------

/** 取得元。 */
export const CostSourceSchema = z.enum(["aws", "github", "openai"]);
export type CostSource = z.infer<typeof CostSourceSchema>;

/**
 * 1 日ぶんの 3 ソース金額。TieredRollupStore の日次パーツ。
 * merge は単純な加算なので可換・結合的 (Store の要求)。
 */
export const CostDailyAggregateSchema = z.object({
  awsUsd: z.number(),
  githubUsd: z.number(),
  openaiUsd: z.number(),
});
export type CostDailyAggregate = z.infer<typeof CostDailyAggregateSchema>;

export const emptyCostDaily = (): CostDailyAggregate => ({
  awsUsd: 0,
  githubUsd: 0,
  openaiUsd: 0,
});

export const mergeCostDaily = (
  a: CostDailyAggregate,
  b: CostDailyAggregate,
): CostDailyAggregate => ({
  awsUsd: a.awsUsd + b.awsUsd,
  githubUsd: a.githubUsd + b.githubUsd,
  openaiUsd: a.openaiUsd + b.openaiUsd,
});

/**
 * 日次コストの 1 点 (保存前の中間表現)。
 *
 * ⚠ **累計ではなく「その日ぶん」**。ロールアップは日次パーツを足して週次・月次を
 * 作るので、当月累計を入れると二重計上になる。
 *
 * ⚠ 取れなかったソースは **0 ではなく undefined**。0 を入れると「その日は使って
 * いない」と「取得できなかった」が区別できなくなり、保存時に既存値を 0 で
 * 潰してしまう。
 */
export const DailyCostPointSchema = z.object({
  /** YYYY-MM-DD (UTC 日。AWS の課金日境界に合わせる)。 */
  date: z.string(),
  awsUsd: z.number().optional(),
  /** GitHub は netAmount (実請求額)。無料枠で相殺された分は含まない。 */
  githubUsd: z.number().optional(),
  openaiUsd: z.number().optional(),
});
export type DailyCostPoint = z.infer<typeof DailyCostPointSchema>;

// ---------------------------------------------------------------------------
// サマリ (Slack のセクションと画面のカード、両方の素)
// ---------------------------------------------------------------------------

/** 通知に出す使用量 1 項目。quantity は割り戻し済み。 */
export const UsageMetricSchema = z.object({
  unit: z.string(),
  /** "xlarge" / "gp3" / "WebACL" 等。無ければ空文字。 */
  label: z.string(),
  quantity: z.number(),
  usd: z.number(),
});
export type UsageMetric = z.infer<typeof UsageMetricSchema>;

export const ServiceCostSchema = z.object({
  /** CE の生のサービス名。ログとの突き合わせ・Tax 判定に使う。 */
  name: z.string(),
  /** 通知に出す略称。 */
  displayName: z.string(),
  usd: z.number(),
  /** 最大 2 件。Tax のように使用量を持たないサービスでは空。 */
  usage: z.array(UsageMetricSchema),
});
export type ServiceCost = z.infer<typeof ServiceCostSchema>;

/**
 * アカウント 1 つぶんの内訳 (PRODUCT_DEV-869)。
 *
 * 分割後は「Umeee の AWS 料金」が 10 アカウントに散っており、合計だけでは
 * どこで増えたのかが分からない。CE の `LINKED_ACCOUNT` グループがそのまま
 * この 1 要素になる。
 */
export const AccountServiceCostSchema = z.object({
  /** CE の生のサービス名。 */
  name: z.string(),
  /** 通知・画面に出す略称 (`aliasService` 済み)。 */
  displayName: z.string(),
  usd: z.number(),
});
export type AccountServiceCost = z.infer<typeof AccountServiceCostSchema>;

export const AccountCostSchema = z.object({
  /** 12 桁のアカウント ID。 */
  accountId: z.string(),
  /**
   * アカウント名。`organizations:ListAccounts` の実測値。
   *
   * ⚠ **取得できなかったときは `accountId` と同じ文字列が入る。**
   * 空文字にすると表示側が「名前なし」と「ID しか分からない」を区別できない。
   */
  accountName: z.string(),
  usd: z.number(),
  /** そのアカウント内でのサービス別内訳。金額降順・上位のみ。 */
  services: z.array(AccountServiceCostSchema),
});
export type AccountCost = z.infer<typeof AccountCostSchema>;

export const AwsCostSummarySchema = z.object({
  /** 抽出対象 (しきい値以上) のサービス。降順。 */
  services: z.array(ServiceCostSchema),
  /** しきい値未満も含む全サービスの合計。 */
  totalUsd: z.number(),
  /** 表示用の期間文字列「YYYY-MM-DD～YYYY-MM-DD」。 */
  periodText: z.string(),
  /** 表示から除外したしきい値。 */
  minUsd: z.number(),
  /**
   * Organization のアカウント別内訳 (PRODUCT_DEV-869)。金額降順。
   *
   * ⚠ **`services` とは別の CE クエリの結果**なので、こちらだけ欠けることがある
   * (CE の GroupBy は 2 軸が上限で、`SERVICE`+`USAGE_TYPE` と
   * `LINKED_ACCOUNT`+`SERVICE` を 1 回のクエリでは取れない)。
   * 取れなかったときは undefined。**空配列にしない** —
   * 「1 アカウントも無い」と「取得できなかった」が区別できなくなる。
   *
   * 追加のみの optional なので後方互換 (COST_PAYLOAD_VERSION は 1 のまま)。
   *
   * ⚠ **形が崩れても payload 全体を落とさない (`catch`)。** これは表示専用の
   * 付随情報で、合計 (`totalUsd`) の正本ではない。ここを厳格にすると、
   * 内訳 1 要素の不備で `parseCostLatest` が失敗し、**画面もカードも全部
   * 「データなし」に倒れる**。落とすべきなのは内訳だけ。
   * (関連する教訓: 配列ごと safeParse すると 1 件の不備で全件消える)
   */
  accounts: z.array(AccountCostSchema).optional().catch(undefined),
});
export type AwsCostSummary = z.infer<typeof AwsCostSummarySchema>;

export const RepoUsageSchema = z.object({
  repositoryName: z.string(),
  minutes: z.number(),
  netUsd: z.number(),
});
export type RepoUsage = z.infer<typeof RepoUsageSchema>;

export const ProductUsageSchema = z.object({
  product: z.string(),
  grossUsd: z.number(),
  discountUsd: z.number(),
  netUsd: z.number(),
});
export type ProductUsage = z.infer<typeof ProductUsageSchema>;

/**
 * GitHub Budgets API が返す Actions の予算。
 * ⚠ **金額 (USD) であって分数ではない。**
 */
export const ActionsBudgetSchema = z.object({
  amountUsd: z.number(),
  /** true なら予算到達時に **Actions の実行自体がブロックされる**。 */
  preventFurtherUsage: z.boolean(),
});
export type ActionsBudget = z.infer<typeof ActionsBudgetSchema>;

export const GitHubBillingSummarySchema = z.object({
  org: z.string(),
  /** Actions の使用分数 (unitType が Minutes の項目の合計)。 */
  actionsMinutes: z.number(),
  actionsGrossUsd: z.number(),
  /** 無料枠で相殺された額。頭打ちになった時点で無料枠を使い切っている。 */
  actionsDiscountUsd: z.number(),
  actionsNetUsd: z.number(),
  /** Actions のリポジトリ別内訳。分数降順。 */
  repos: z.array(RepoUsageSchema),
  /** Actions 以外も含む product 別の合計。 */
  products: z.array(ProductUsageSchema),
  totalNetUsd: z.number(),
  /** プラン付属の無料枠 (分)。Team = 3,000。 */
  freeTierMinutes: z.number(),
  /** Actions 分数の単価 (USD/分)。取得できなければ 0。 */
  actionsPricePerMinuteUsd: z.number(),
  budget: ActionsBudgetSchema.optional(),
  /** Linux 以外の Actions sku を検出したか (runner 倍率の注記用)。 */
  hasNonLinuxRunner: z.boolean(),
});
export type GitHubBillingSummary = z.infer<typeof GitHubBillingSummarySchema>;

/**
 * OpenAI の利用区分。
 *
 * ⚠ API キーが Realtime とテキストで共用されているため `api_key_id` では割れず、
 * **モデル名 / line_item から判定するしかない**。判定できなかったものは `other`。
 * `other` を黙って realtime か text へ寄せてはいけない (単価が桁違いなので、
 * 寄せ先次第で内訳の意味が反転する)。
 */
export const OpenAiSegmentSchema = z.enum([
  "realtime",
  "text",
  "transcription",
  "other",
]);
export type OpenAiSegment = z.infer<typeof OpenAiSegmentSchema>;

export const OpenAiSegmentUsageSchema = z.object({
  segment: OpenAiSegmentSchema,
  /** 通知・画面に出す日本語ラベル。 */
  label: z.string(),
  /** 対象月ぶんの額。 */
  usd: z.number(),
  /** 対象月ぶんの入力トークン (Realtime は audio トークンを含む)。 */
  inputTokens: z.number(),
  outputTokens: z.number(),
  requests: z.number(),
  /**
   * この区分に集まった実際の line_item / model 名。
   * `other` の中身を出して分類漏れに気付けるようにするために持つ。
   */
  names: z.array(z.string()),
});
export type OpenAiSegmentUsage = z.infer<typeof OpenAiSegmentUsageSchema>;

/**
 * OpenAI の当月コストと内訳。
 *
 * ⚠ **残高は扱わない。** 残高を返す API が存在せず (未文書化の credit_grants は
 * 2026-08-17 実測で 403 = ブラウザの session key 専用)、しかも auto-recharge
 * なので人が入れた値もすぐ陳腐化する。残高は Billing ページへのリンクで見る。
 */
export const OpenAiCostSummarySchema = z.object({
  monthUsd: z.number(),
  /** 区分別の内訳 (当月)。金額降順。 */
  segments: z.array(OpenAiSegmentUsageSchema),
  /** 金額の付いた未分類があるか (通知・画面に注記を出す)。 */
  hasUnclassifiedCost: z.boolean(),
  /** 期間文字列 (月初～基準日)。 */
  periodText: z.string(),
  /**
   * トークン数だけ取得できなかったか (PRODUCT_DEV-724)。
   *
   * 金額 (costs API) とトークン (usage API) は**別のエンドポイント**で、
   * 2026-08-19 に usage 側だけが HTTP 504 で落ちた。金額が取れているなら
   * 内訳の金額と割合には意味があるので、トークンを諦めて表示を続ける。
   *
   * ⚠ このとき `inputTokens` 等は 0 になるが、**それは「使っていない」ではない**。
   * 表示側は 0 と出さず「取得できませんでした」と言うこと。
   *
   * 追加のみの optional なので後方互換 (COST_PAYLOAD_VERSION は 1 のまま)。
   * 未設定 = 平常。
   */
  tokensUnavailable: z.boolean().optional(),
});
export type OpenAiCostSummary = z.infer<typeof OpenAiCostSummarySchema>;

// ---------------------------------------------------------------------------
// latest.json — Slack と画面カードの共通の素
// ---------------------------------------------------------------------------

/**
 * 直近実行時のサマリ一式。**これが Slack の各セクションの素であり、
 * 同時に画面カードの素**でもある。
 *
 * 各ソースは fail-soft なので欠けうる。取れたものは `sources` に入る
 * (「0 だった」と「取得できなかった」を区別するため、欠けたソースは
 * フィールドごと undefined にする)。
 */
export const CostLatestSnapshotSchema = z.object({
  /** payload のスキーマ版。読み手は自分の知る版と一致するかを見る。 */
  version: z.number(),
  /** 集計の基準日 (前日) YYYY-MM-DD。 */
  refDate: z.string(),
  /** 保存時刻 ISO8601。 */
  generatedAt: z.string(),
  /** 取得に成功したソース。 */
  sources: z.array(CostSourceSchema),
  aws: AwsCostSummarySchema.optional(),
  github: GitHubBillingSummarySchema.optional(),
  openai: OpenAiCostSummarySchema.optional(),
});
export type CostLatestSnapshot = z.infer<typeof CostLatestSnapshotSchema>;

/**
 * 読み手用のパース。**版が違えば読まない。**
 *
 * ops が先にデプロイされて新形式を書き、admin がまだ旧版、という瞬間は必ず
 * 起きる。そこで誤った数字を出すより「データなし」と言うほうが安全。
 *
 * @returns 読めた snapshot。読めなければ null と理由。
 */
export function parseCostLatest(
  raw: unknown,
): { ok: true; value: CostLatestSnapshot } | { ok: false; reason: string } {
  const parsed = CostLatestSnapshotSchema.safeParse(raw);
  if (!parsed.success) {
    return {
      ok: false,
      reason: `schema mismatch: ${parsed.error.issues
        .slice(0, 3)
        .map((i) => `${i.path.join(".")}: ${i.message}`)
        .join(", ")}`,
    };
  }
  if (parsed.data.version !== COST_PAYLOAD_VERSION) {
    return {
      ok: false,
      reason: `version mismatch: got ${parsed.data.version}, expected ${COST_PAYLOAD_VERSION}`,
    };
  }
  return { ok: true, value: parsed.data };
}
